import './style.scss';

import { Container } from '@design-system/container';
import { SearchResultsHeader } from '@design-system/search-results-header';
import { Tab, Tabs } from '@design-system/tabs';
import classNames from 'classnames';
import React, { FC, useEffect, useState } from 'react';

import { gtmService } from '~/services/gtm';
import { isBrowser } from '~/utils/browser';
import getQueryParamByName from '~/utils/getQueryParamByName';

import ArticleListing from './ArticleListing';
import { SearchingState, SearchResultsProps, TabsVariant } from './models';
import ProductListing from './ProductListing';
import { executeSearch } from './SearchService/executeSearch';
import { getPopularSearches } from './SearchService/getPopularSearches';
import { SearchResults as SearchResultsData } from './SearchService/types';
import SearchResultSkeleton from './Skeleton';

const SearchResults: FC<SearchResultsProps> = ({
  resultsTab,
  productTab,
  articleTab,
  moreResultsButtonLabel,
  moreProductsButtonLabel,
  moreArticlesButtonLabel,
  initialVisibleItems,
  header,
  imageSettings,
  fluidGrid,
  locale,
  uid,
  componentId,
}) => {
  const {
    mode,
    description,
    descriptionSize,
    phraseTag,
    phraseSize,
    hiddenHeadline,
    popularSearchesDescription,
    popularSearchesSize,
  } = header;

  const [searchResults, setSearchResults] = useState<SearchResultsData>({
    articles: [],
    products: [],
  });
  const [popularSearches, setPopularSearches] = useState<Array<string> | null>(
    null
  );

  const [isLoading, setIsLoading] = useState(true);
  const [tabValue, setTabValue] = useState(0);
  const [searchingState, setSearchingState] = useState<SearchingState>(
    SearchingState.pending
  );

  const searchQuery = isBrowser() && decodeURI(getQueryParamByName('q') ?? '');

  const handleTabChange = (newValue: number) => {
    setTabValue(newValue);
  };

  useEffect(() => {
    if (searchQuery) {
      gtmService.emitViewSearchResults(searchQuery);

      executeSearch(searchQuery, locale).then((searchResultsData) => {
        setSearchResults(searchResultsData);

        if (searchResults.products.length > 0) {
          gtmService.emitViewItemList(
            searchResults.products.map((product) => ({
              heroImage: {
                url: product.image.url,
                alt: product.image.title,
                localAsset: { childImageSharp: product.image.data },
              },
              brandName: product.brandName,
              salsifyDigitalAssets: [],
              productTitle: product.title,
              descriptionShort: product.description,
              descriptionLong: '',
              ingredients: '',
              directionsForUse: '',
              safetyWarnings: '',
              netContentStatement: product.netContentStatement,
              aromaScent: product.aromaScent,
              heroImageID: '',
              websiteImagesID: [],
              barcodeNumber: product.barcodeNumber,
              sku: product.sku,
              segmentDescription: '',
            }))
          );
        }

        if (
          searchResultsData.articles.length +
            searchResultsData.products.length >
          0
        ) {
          setSearchingState(SearchingState.results);
        } else {
          setSearchingState(SearchingState.noResults);

          if (popularSearches == null) {
            getPopularSearches().then(
              (popularSearchesData) =>
                popularSearchesData && setPopularSearches(popularSearchesData)
            );
          }
        }
      });
    } else {
      setSearchingState(SearchingState.emptyPhrase);
    }

    setIsLoading(false);
  }, [searchQuery, locale]);

  const getTabTitle = (tabName, resultCount) => (
    <span className="tab-title">
      <span>{`${tabName}`}</span>
      &nbsp;
      <span>{`(${resultCount})`}</span>
    </span>
  );

  return (
    <div id={componentId} data-component-uid={uid}>
      <SearchResultsHeader
        mode={mode}
        state={searchingState}
        description={description}
        descriptionSize={descriptionSize}
        phrase={searchQuery || undefined}
        phraseTag={phraseTag}
        phraseSize={phraseSize}
        hiddenHeadline={hiddenHeadline}
        popularSearches={popularSearches}
        popularSearchesDescription={popularSearchesDescription}
        popularSearchesSize={popularSearchesSize}
      />
      {searchingState === SearchingState.pending ? (
        <SearchResultSkeleton initialVisibleItems={initialVisibleItems} />
      ) : null}
      {searchingState === SearchingState.results ? (
        <Container
          className={classNames('search-result', {
            'search-result--loaded': !isLoading,
          })}
          maxWidth="max-grid"
        >
          <Tabs value={tabValue} onChange={handleTabChange}>
            <Tab
              key={resultsTab}
              title={getTabTitle(
                resultsTab,
                searchResults.articles.length + searchResults.products.length
              )}
            >
              <ProductListing
                items={searchResults.products}
                maxVisibleItems={initialVisibleItems}
                setTabValue={setTabValue}
                tabNumber={TabsVariant.productsTab}
                moreButtonText={moreProductsButtonLabel}
                imageSettings={imageSettings}
                fluidGrid={fluidGrid}
              />
              <ArticleListing
                items={searchResults.articles}
                maxVisibleItems={initialVisibleItems}
                setTabValue={setTabValue}
                tabNumber={TabsVariant.articlesTab}
                moreButtonText={moreArticlesButtonLabel}
              />
            </Tab>
            <Tab
              key={productTab}
              title={getTabTitle(productTab, searchResults.products.length)}
            >
              <ProductListing
                items={searchResults.products}
                setTabValue={setTabValue}
                tabNumber={TabsVariant.allResults}
                moreButtonText={moreResultsButtonLabel}
                imageSettings={imageSettings}
                fluidGrid={fluidGrid}
              />
            </Tab>
            <Tab
              key={articleTab}
              title={getTabTitle(articleTab, searchResults.articles.length)}
            >
              <ArticleListing
                items={searchResults.articles}
                setTabValue={setTabValue}
                tabNumber={TabsVariant.allResults}
                moreButtonText={moreResultsButtonLabel}
              />
            </Tab>
          </Tabs>
        </Container>
      ) : null}
    </div>
  );
};

export default SearchResults;
